/**
 * Cookie-backed MAPS partner session. NO localStorage anywhere — the HttpOnly
 * `maps_session` cookie is the single source of truth. We just call the
 * server to validate/redeem; the browser handles cookie persistence.
 */
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { firstValueFrom } from 'rxjs';
import {
    MAPS_ACCESS_VALIDATE,
    MAPS_LOGOUT,
    MAPS_SESSION_VALIDATE,
} from './maps-graphql/queries';
import type { MapsAccessResult, MapsSessionStatus } from '../models/maps.types';

@Injectable({ providedIn: 'root' })
export class MapsSessionService {
    constructor(private readonly apollo: Apollo) {}

    async RedeemAccessKey(accessKey: string): Promise<MapsAccessResult> {
        const result = await firstValueFrom(
            this.apollo.mutate<{ mapsAccessValidate: MapsAccessResult }>({
                mutation: MAPS_ACCESS_VALIDATE,
                variables: { accessKey },
            })
        );
        if (!result.data?.mapsAccessValidate) {
            throw new Error('mapsAccessValidate returned empty payload');
        }
        return result.data.mapsAccessValidate;
    }

    async Validate(): Promise<MapsSessionStatus> {
        const result = await firstValueFrom(
            this.apollo.query<{ mapsSessionValidate: MapsSessionStatus }>({
                query: MAPS_SESSION_VALIDATE,
                fetchPolicy: 'network-only',
            })
        );
        return (
            result.data?.mapsSessionValidate ?? {
                valid: false,
                isAdmin: false,
                showPipelinePanelToAll: false,
                defaultResultCount: 0,
                // Fail open: career-stage display defaults to on.
                showCareerStage: true,
                contactSendCap: 0,
            }
        );
    }

    async Logout(): Promise<void> {
        await firstValueFrom(this.apollo.mutate({ mutation: MAPS_LOGOUT }));
    }
}
